// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Override this component to implement:
 * - Lazy loading feature: Images will be loaded when they are visible in viewports
 * - Responsive images using picture element
 */
import { IconPresentationProps } from "@insite/mobius/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp, injectCss, resolveColor } from "@insite/mobius/utilities";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import parseImageUrl from "@insite/mobius/utilities/parseImageUrl";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { getSettingsCollection } from "@vanmeter/App/Store/Selectors/SettingsSelectors";
import { ImageSettingKey } from "@vanmeter/XDL/types";
import { color } from "@vanmeter/XDL/Utilities";
import { getCloudinaryUrl, TransformationsOptions } from "@vanmeter/XDL/Utilities/cloudinary";
import qs from "qs";
import React from "react";
import { connect } from "react-redux";
import styled, { withTheme } from "styled-components";

export const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const mediaRootPath = (getSettingsCollection(state).customSettings as any).mediaRootPath;
    const {
        smallDefaultProductImage,
        mediumDefaultProductImage,
        largeDefaultProductImage,
        defaultCategoryImage,
        defaultBrandImage,
    } = getSettingsCollection(state).customSettings;

    const generalNotFoundImagePath = smallDefaultProductImage || mediumDefaultProductImage || largeDefaultProductImage;
    const notFoundImagePath =
        ownProps.imageKey === ImageSettingKey.category
            ? defaultCategoryImage || generalNotFoundImagePath
            : ownProps.imageKey === ImageSettingKey.brand
              ? defaultBrandImage || generalNotFoundImagePath
              : generalNotFoundImagePath;

    return { mediaRootPath, notFoundImagePath };
};

export interface LazyImagePresentationProps {
    /** CSS string or styled-components function to be injected into this component.
     * @themable */
    css?: StyledProp<LazyImageProps>;
    /** Optional content to show while image is loading.
     * @themable */
    placeholder?: React.ReactNode;
    /** The height of the image.
     * @themable */
    height?: string;
    /** The width of the image.
     * @themable */
    width?: string;
    /** Props passed to the icon component that displays when the image fails to load.
     * @themable */
    errorIconProps?: IconPresentationProps;
    /** Props passed to the typography component that displays when the image fails to load.
     * @themable */
    errorTypographyProps?: TypographyPresentationProps;
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
}

export type LazyImageProps = MobiusStyledComponentProps<
    "div",
    LazyImagePresentationProps & {
        /** Props to be passed into the inner `img` element. */
        imgProps?: JSX.IntrinsicElements["img"];
        /** The URL to fetch the image from. If not provided, LazyImage renders `null`. */
        src?: string;
        /** The alternative text to display in place of the image. */
        altText?: string;
        /** Callback function that will be called when the image has finished loading. */
        onLoad?: (newImagePath?: string) => void;
        /** Callback function that will be called if an error occurs when loading an image. */
        onError?: () => void;
        /** Allows to detect pdf generation */
        isShareEntity?: boolean;
    }
>;

// if the image loads in under this number of milliseconds, it doesn't fade in.
const fadeInThreshold = 100;

type State = {
    src?: string;
    loaded: boolean;
    error: boolean;
    startTime: number;
    imageShouldFade: boolean;
    showPlaceholder: boolean;
};

const LazyImageStyle = styled.div<
    Pick<State & OwnProps, "error" | "imageShouldFade" | "loaded" | "isCdnUrl" | "width" | "height"> & {
        isShareEntity?: boolean;
    }
>`
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: ${({ error, theme }) => (error ? resolveColor("common.accent", theme) : null)};
    ${({ error }) => error && "padding: 5px;"}
    width: ${getProp("width")};
    height: ${getProp("height")};
    overflow: hidden;
    img {
        width: ${({ isCdnUrl, width }) => isCdnUrl && width && `${width}`};
        height: ${({ height }) => height && `${height}`};
        will-change: opacity;
        transition: opacity ${({ imageShouldFade }) => (imageShouldFade ? ".2s ease" : "0s")};
        ${({ loaded, isShareEntity }) => (isShareEntity ? "" : loaded ? "opacity: 1;" : "opacity: 0;")}
        ${({ error }) => error && "padding: 3px;"}
    }
    .LazyImage-Placeholder {
        width: 100%;
        height: 100%;
        position: absolute;
        will-change: opacity;
        transition: opacity ${({ imageShouldFade }) => (imageShouldFade ? ".2s ease" : "0s")};
        ${({ loaded, isShareEntity }) => (isShareEntity ? "" : loaded ? "opacity: 0;" : "opacity: 1;")}
        @keyframes shine {
            to {
                background-position-x: -200%;
            }
        }
        background: linear-gradient(
            110deg,
            ${color("common.ui2")} 8%,
            ${color("common.ui1")} 18%,
            ${color("common.ui2")} 33%
        );
        animation: 2s shine linear infinite;
        background-size: 200% 100%;
    }
    p {
        text-align: center;
        display: inline-block;
        padding: 3px;
        width: 100%;
        overflow-wrap: break-word;
        word-wrap: break-word;
    }
    ${injectCss}
`;

export type OwnProps = LazyImageProps & {
    // Props to be passed into the inner `picture` element.
    pictureProps?: JSX.IntrinsicElements["picture"];
    /** </xng-change> */
    // The URL to fetch the image from for small viewports
    smallImageSrc?: string;
    // The URL to fetch the image from for medium viewports
    mediumImageSrc?: string;
    // The URL to fetch the image from for large viewports
    largeImageSrc?: string;
    // use This flag to disable image lazyLoading
    disableLazyLoad?: boolean;
    // specify if using CdnUrl or not
    isCdnUrl?: boolean;
    /** Props passed to the Image URL
     * @themable */
    width?: string;
    height?: string;
    quality?: number;
    /** </xng-change> */
    // XNG-Changes: Set fallback image when image not found
    fallbackImageSrc?: string;
    // XNG-Changes: Ignore fallback images when true
    disableFallbackImage?: boolean;
    // xng-change
    imageKey?: string;
};

export type Props = OwnProps & ReturnType<typeof mapStateToProps>;

/**
 * Defers loading of an image until after its first render. If the asset takes over 100ms to load, the image is faded
 * into view.
 */
export class LazyImage extends React.Component<Props, State> {
    imageRef: React.RefObject<HTMLImageElement | null>;
    _mounted = false;

    constructor(props: Props) {
        super(props);
        this.imageRef = React.createRef<HTMLImageElement>();

        const queryString = props.src && /\?.*/.exec(props.src);

        // XNG-Changes
        if ((props.width && props.width !== "undefined") || (props.height && props.height !== "undefined")) {
            this.width = props.width;
            this.height = props.height;
        } else if (queryString) {
            const obj: { width?: string; height?: string } = qs.parse(queryString[0], { ignoreQueryPrefix: true });

            this.width = obj.width;
            this.height = obj.height;
        } else {
            this.width = "";
            this.height = "";
        }

        if (props.src) {
            this.srcWithoutQuery = this.replaceSrc(props.src);
        }
    }

    // XNG-Changes: fix typing issue
    // UNSAFE_componentWillUpdate(nextProps: Readonly<LazyImageProps>): void {
    //     if (nextProps.src) {
    //         this.srcWithoutQuery = this.replaceSrc(nextProps.src);
    //     }
    // }

    image?: HTMLImageElement;
    fadeInTimeout?: NodeJS.Timeout;
    width?: number | string;
    height?: number | string;
    srcWithoutQuery?: string;

    state: State = {
        loaded: false,
        error: false,
        startTime: 0,
        imageShouldFade: false,
        showPlaceholder: true,
    };
    replaceSrc(src?: string) {
        if (src) {
            return parseImageUrl(src).url;
        }
    }

    /** <xng-change> */

    // use this when project has Sirv Image
    checkIfSirvBaseUrl = (baseUrl: string) => {
        return baseUrl?.toLowerCase().includes(".sirv.");
    };

    // in Case of CDN Path use this function to get img URL
    getImgUrl = (src?: string, transformations?: TransformationsOptions) => {
        // XNG-Changes: Handle base64 images directly
        if (src?.startsWith("data:") || src?.match(/^[A-Za-z0-9+/]{20,}={0,2}$/)) {
            const isBase64WithPrefix = src.startsWith("data:");
            return isBase64WithPrefix ? src : `data:image/bmp;base64,${src}`;
        }

        const imgSrc =
            src || this.props.src || (!this.props.disableFallbackImage ? this.props.notFoundImagePath : "") || "";
        if (!imgSrc) {
            return imgSrc;
        }
        if (this.props.isCdnUrl) {
            return getCloudinaryUrl(imgSrc, this.props.mediaRootPath, transformations) || imgSrc;
        }
        // if not CDN
        return this.getImageSrc(src);
    };

    // in case of SIRV Path use this function.
    getImageSrc = (src?: string) => {
        // Local/relative paths should not go through CDN transformation
        if (src?.startsWith("/") || src?.startsWith("./")) return src;
        const { mediaRootPath, notFoundImagePath, width, quality } = this.props;
        const isSirvBaseUrl = this.checkIfSirvBaseUrl(mediaRootPath);
        if (
            src &&
            (src.toLowerCase().indexOf("wwww.") > -1 ||
                (src.toLowerCase().indexOf("http") > -1 && src.toLowerCase().indexOf("res.") === -1) ||
                src.toLowerCase().indexOf("userfiles") > -1)
        ) {
            return src;
        }
        if (src && mediaRootPath && isSirvBaseUrl) {
            return `${mediaRootPath}Images/${src}.jpg?format=optimal&q=${quality || 70}&webp.fallback=jpg&width=${
                width || "auto"
            }`;
        }
        if (src && mediaRootPath && !isSirvBaseUrl) {
            return `${mediaRootPath}f_auto,w_${width || "auto"},q_${quality || 70}/${src}.jpg`;
        }
        //  we need to add not found image path hear
        return notFoundImagePath;
    };

    /** <xng-change> */
    onLoadHandler = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
        event.stopPropagation();
        this.setState(
            currentState => ({
                // fade if image takes more than 100ms to load (i.e. not cached)
                imageShouldFade: Date.now() - currentState.startTime > fadeInThreshold,
                loaded: true,
                src: this.props.src,
            }),
            () => {
                this.props.onLoad?.(this.imageRef.current?.currentSrc);
            },
        );
    };

    onErrorHandler = () => {
        this.setState(
            {
                loaded: true,
                error: true,
            },
            this.props.onError,
        );
    };
    /** </xng-change> */

    reloadImage() {
        this.setState({ startTime: Date.now(), loaded: false, error: false });
        this.imageRef.current?.classList.add("lazyload");
        this.fadeInTimeout = setTimeout(() => {
            this.setState({ showPlaceholder: true });
        }, fadeInThreshold);
    }

    componentDidMount() {
        this.reloadImage();
        this._mounted = true;
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.src !== prevProps.src) {
            this.reloadImage();
        }
    }

    componentWillUnmount() {
        this._mounted = false;
        clearTimeout(this.fadeInTimeout);
    }

    /** <xng-change> */
    hasInvalidProps() {
        return !this.props.src && !this.props.fallbackImageSrc && !this.props.notFoundImagePath;
    }

    /**
     *
     * @param currentTarget
     * @returns
     * a boolean to check if we should call onErrorHandler passed to LazyImage Component
     * DisableFallBackImage => Ignore fallback images when true and call onErrorHandler directly
     * then it checks if we couldn't create a fallBackImage or the image src already matches the fallBackImageSrc
     * then it checks if the previous line condition occures for defaultFallbackImageSrc
     * then if isCategoryImage flag is true make image src customSetting.categoryDefaultImage
     */
    shouldCallOnErrorHandler(currentTarget: any): boolean {
        const { notFoundImagePath, fallbackImageSrc, disableFallbackImage } = this.props;

        return (
            disableFallbackImage ||
            ((!fallbackImageSrc || currentTarget.src.includes(fallbackImageSrc)) &&
                (!notFoundImagePath || currentTarget.src.includes(notFoundImagePath)))
        );
    }

    renderImage(src?: string, fromPic?: boolean) {
        let { src: propSrc } = this.props;
        const { imgProps, altText, notFoundImagePath, fallbackImageSrc } = this.props;
        const { error, loaded, src: stateSrc } = this.state;
        propSrc = src || propSrc;
        if (this.height === "100%" && this.width === "auto") {
            const queryString = propSrc && /\?.*/.exec(propSrc);
            if (queryString) {
                const obj: { width?: string; height?: string } = qs.parse(queryString[0], { ignoreQueryPrefix: true });
                this.width = obj.width;
                this.height = obj.height;
            }
        }

        return (
            <img
                ref={this.imageRef}
                alt={altText}
                className="lazyload"
                width={this.width}
                height={this.height}
                onLoad={this.onLoadHandler}
                data-src={loaded && !error ? this.getImgUrl(stateSrc) : this.getImgUrl(propSrc)}
                onError={({ currentTarget }) => {
                    if (this.shouldCallOnErrorHandler(currentTarget)) {
                        this.onErrorHandler();
                        return;
                    }
                    currentTarget.onerror = null;
                    currentTarget.src = (fallbackImageSrc || notFoundImagePath) as string;
                }}
                {...(imgProps as
                    | React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>
                    | undefined)}
            />
        );
    }
    /** </xng-change> */

    render() {
        const {
            mergeCss,
            src: propSrc,
            placeholder,
            imgProps,
            altText,
            isShareEntity,
            disableLazyLoad,
            ...otherProps
        } = this.props;
        const { error, imageShouldFade, loaded, showPlaceholder, src: stateSrc } = this.state;
        const { applyStyledProp, spreadProps } = applyPropBuilder(this.props, { component: "lazyImage" });
        const resolvedMergeCss = mergeCss ?? this.props?.theme?.lazyImage?.defaultProps?.mergeCss;

        /** <xng-change> */
        if (this.hasInvalidProps()) {
            return null;
        }
        /** </xng-change> */
        if (disableLazyLoad || isShareEntity) {
            return (
                <LazyImageStyle
                    {...{ error, imageShouldFade, loaded: true, isShareEntity }}
                    {...otherProps}
                    css={applyStyledProp("css", resolvedMergeCss)}
                >
                    <img
                        className="lazyloaded"
                        width={this.width}
                        height={this.height}
                        alt={altText}
                        src={this.getImgUrl(propSrc)}
                        {...(imgProps as
                            | React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>
                            | undefined)}
                    />
                </LazyImageStyle>
            );
        }

        return (
            <LazyImageStyle
                {...{ error, imageShouldFade, loaded, isShareEntity }}
                {...otherProps}
                css={applyStyledProp("css", resolvedMergeCss)}
            >
                {showPlaceholder && <span className="LazyImage-Placeholder">{placeholder}</span>}
                {/* {loaded && error && <IconMemo {...spreadProps("errorIconProps")} title={altText} />} */}
                {loaded && error && this._mounted && this.renderImage(this.props.notFoundImagePath)}
                {/* XNG-Changes: add lazyload + show image on client side only  */}
                {!error && /** <xng-change> */ this._mounted && this.renderImage() /** </xng-change> */}
                {/* {error && (
                    <Typography {...spreadProps("errorTypographyProps")} as="p">
                        {altText}
                    </Typography>
                )} */}
            </LazyImageStyle>
        );
    }

    static defaultProps = {
        height: "100%",
        width: "auto",
    };
}

/** @component */

export default withTheme(connect(mapStateToProps)(LazyImage));

export { LazyImageStyle };
